import type { StageCollisions } from '@/state/pipelines/types';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { cn } from 'helpers/cn';

type CollisionNoticeProps = {
  collisions: StageCollisions | null;
};

/**
 * What is wrong with one column, per pair.
 *
 * Names both experiments and what they collide over. "This stage has a conflict" leaves
 * someone to work out which two of six cards caused it, and separating the wrong pair
 * costs a whole stage of runtime to discover.
 */
export const CollisionNotice = ({ collisions }: CollisionNoticeProps) => {
  if (!collisions || collisions.pairs.length === 0) return null;

  return (
    <div className="space-y-2">
      {collisions.pairs.map((pair) => {
        const isBlock = pair.collision.verdict === 'block';
        const Icon = isBlock ? ShieldAlert : AlertTriangle;

        return (
          <div
            key={`${pair.aId}-${pair.bId}`}
            className={cn(
              'rounded-card border p-2 text-micro leading-[1.5]',
              isBlock ? 'bg-danger-bg border-danger-line text-danger' : 'border-warn-line text-warn',
            )}
          >
            <div className="flex items-start gap-1.5">
              <Icon className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.5} />
              <div className="min-w-0 space-y-1">
                <p className="font-medium">
                  {pair.aName} &amp; {pair.bName}
                </p>
                <p>{pair.collision.reason}</p>
                {pair.collision.detail.length > 0 && (
                  <p className="text-dim font-mono">{pair.collision.detail.slice(0, 4).join(', ')}</p>
                )}
                {isBlock && <p className="font-medium">The engine will not start this stage.</p>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
